import React from "react";
import { AbsenceRequestResponseDto } from "../../../types/absence";
import { DataTable, Column, Action } from "../../../components/common/DataTable/DataTable";
import {
  AbsenceStatusChip,
  AbsenceCategoryChip,
  DateRangeDisplay,
} from "../../../components/absence";
import { Cancel as CancelIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import { AbsenceRequestStatus } from "../../../constants/absenceEnums";
import { getAbsenceCategoryColor, getAbsenceCategoryName, getAbsenceStatusColor, getAbsenceStatusName } from "../../../utils/statusColors";

interface MyAbsenceRequestsListProps {
  requests: AbsenceRequestResponseDto[];
  isLoading: boolean;
  onCancelClick: (request: AbsenceRequestResponseDto) => void;
  onViewDetailsClick: (request: AbsenceRequestResponseDto) => void;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const MyAbsenceRequestsList: React.FC<MyAbsenceRequestsListProps> = ({
  requests,
  isLoading,
  onCancelClick,
  onViewDetailsClick,
  totalCount,
  hasNextPage,
  isLoadingMore,
  onLoadMore,
}) => {
  const truncateText = (text: string | null, maxLength: number = 50): string => {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };
  const columns: Column[] = [
    {
      field: "absenceCategory",
      headerName: "Type",
      flex: 1,
      renderCell: (value: number) => (
        <span style={{ 
          color: getAbsenceCategoryColor(value),
          fontWeight: 'bold'
        }}>
          {getAbsenceCategoryName(value)}
        </span>
      ),
    },
    {
      field: "startDate",
      headerName: "Dates",
      flex: 2,
      renderCell: (value: string, row: AbsenceRequestResponseDto) => (
        <DateRangeDisplay
          startDate={row.startDate}
          endDate={row.endDate}
          days={row.daysDeducted}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (value: number) => (
        <span style={{ 
          color: getAbsenceStatusColor(value),
          fontWeight: 'bold'
        }}>
          {getAbsenceStatusName(value)}
        </span>
      ),
    },
    {
      field: "requestReason",
      headerName: "Reason",
      flex: 1.5,
      renderCell: (value: string | null) => truncateText(value, 40),
    },
  ];

  const getActions = (request: AbsenceRequestResponseDto): Action<AbsenceRequestResponseDto>[] => {
    const actions: Action<AbsenceRequestResponseDto>[] = [];

    // Always show view details button
    actions.push({
      icon: <VisibilityIcon />,
      label: "View Details",
      onClick: onViewDetailsClick,
      color: "primary",
    });

    // Only allow cancellation if request is pending
    if (request.status === AbsenceRequestStatus.Pending) {
      actions.push({
        icon: <CancelIcon />,
        label: "Cancel Request",
        onClick: onCancelClick,
        color: "warning",
      });
    }

    return actions;
  };

  return (
    <DataTable
      items={requests}
      columns={columns}
      actions={getActions}
      isLoading={isLoading}
      totalCount={totalCount}
      hasNextPage={hasNextPage}
      isLoadingMore={isLoadingMore}
      onLoadMore={onLoadMore}
    />
  );
};

export default MyAbsenceRequestsList;


